<div class="justup-dialog-large">
  <vl-header-dialog (close)="dialogRef.close()" [rightElement]="excelBtn">
    {{ 'dashboard_ui.evolution_valeur_title' | trans }}
    <ng-container extras>
      <span class="subheading-2 on-accent">{{ totalValeur() }}</span>
      @if (deltaText()) {
        <span class="delta-badge"
              [class.delta-positive]="deltaClass() === 'positive'"
              [class.delta-negative]="deltaClass() === 'negative'">
          {{ deltaText() }}
        </span>
      }
    </ng-container>
  </vl-header-dialog>

  <ng-template #excelBtn>
    <vl-excel-export-button size="medium"
                            [loading]="exporting()"
                            [disabled]="exporting()"
                            [tooltip]="'dashboard_ui.evolution_valeur_export' | trans"
                            (action)="exportExcel()"/>
  </ng-template>

  <div class="justup-dialog-content flex-col gap-16">
    <!-- Sélecteurs -->
    <div class="flex-row gap-16 wrap align-center">
      <div class="flex-col gap-4">
        <span class="tiny on-background-alt">{{ 'dashboard_ui.evolution_valeur_field_range' | trans }}</span>
        <vl-chips-toggle [elements]="rangeElements" [(value)]="range" size="small"/>
      </div>
      <div class="flex-col gap-4">
        <span class="tiny on-background-alt">{{ 'dashboard_ui.evolution_valeur_field_granularity' | trans }}</span>
        <vl-chips-toggle [elements]="granularityElements()" [(value)]="granularity" size="small"/>
      </div>
      <div class="flex-col gap-4">
        <span class="tiny on-background-alt">{{ 'dashboard_ui.evolution_valeur_field_dimension' | trans }}</span>
        <vl-chips-toggle [elements]="dimensionElements" [(value)]="dimension" size="small"/>
      </div>
      @if (dimension() !== 'total' && filterElements().length > 0) {
        <div class="flex-col gap-4">
          <span class="tiny on-background-alt">{{ 'dashboard_ui.evolution_valeur_field_filter' | trans }}</span>
          <vl-multi-checkbox
            [elements]="filterElements()"
            [(value)]="selectedKeys"
            [text]="filterLabel()"
            [showNumber]="true"
            [showTextFilter]="true"/>
        </div>
      }
      <div class="grow"></div>
      <button type="button"
              class="recompute-btn"
              [disabled]="refreshing()"
              [matTooltip]="'dashboard_ui.evolution_valeur_refresh' | trans"
              (click)="refreshNow()">
        <mat-icon [class.spinning]="refreshing()">sync</mat-icon>
        <span>{{ 'dashboard_ui.evolution_valeur_recompute' | trans }}</span>
      </button>
    </div>

    @if (needsBackfill()) {
      <div class="banner-info flex-row gap-8 align-center">
        <mat-icon>info_outline</mat-icon>
        <span class="grow">{{ 'dashboard_ui.evolution_valeur_partial_warning' | trans }}</span>
        <button type="button"
                class="banner-btn"
                [disabled]="refreshing()"
                (click)="refreshNow()">
          {{ 'dashboard_ui.evolution_valeur_recompute' | trans }}
        </button>
      </div>
    }

    <!-- Chart -->
    <div class="chart-container">
      @if (loading() && !payload()) {
        <ngx-skeleton-loader count="1" [theme]="{'height': '320px', 'background-color': 'var(--color-on-disabled)'}"/>
      } @else if ((payload()?.total?.length ?? 0) > 0) {
        <canvas #chart></canvas>
      } @else {
        <div class="flex-col gap-8 align-center justify-center" style="height: 100%; gap: 8px">
          <mat-icon class="empty-icon">show_chart</mat-icon>
          <span class="caption on-background-alt">{{ 'dashboard_ui.evolution_valeur_empty' | trans }}</span>
          <span class="tiny on-disabled">{{ 'dashboard_ui.evolution_valeur_empty_hint' | trans }}</span>
        </div>
      }
    </div>

    <!-- Tableau (uniquement si dimension != total) -->
    @if (dimension() !== 'total' && visibleSeries().length) {
      <div class="dim-table">
        <div class="dim-row dim-header">
          <span class="dim-cell color-cell"></span>
          <span class="dim-cell grow">{{ 'dashboard_ui.evolution_valeur_col_dim' | trans }}</span>
          <span class="dim-cell numeric">{{ 'dashboard_ui.evolution_valeur_col_current' | trans }}</span>
          <span class="dim-cell numeric">{{ 'dashboard_ui.evolution_valeur_col_delta' | trans }}</span>
        </div>
        @for (s of visibleSeries(); track s.dimensionKey) {
          <div class="dim-row"
               [class.dim-row--clickable]="canExpandRow(s.dimensionLabel)"
               (click)="onRowClick(s.dimensionLabel)">
            <span class="dim-cell color-cell">
              @if (canExpandRow(s.dimensionLabel)) {
                <mat-icon class="expand-chevron"
                          [class.expand-chevron--open]="isExpanded(s.dimensionLabel)">chevron_right</mat-icon>
              } @else {
                <span class="color-dot" [style.background-color]="s.color"></span>
              }
            </span>
            <span class="dim-cell grow ellipsis" [matTooltip]="s.dimensionLabel">{{ s.dimensionLabel }}</span>
            <span class="dim-cell numeric">{{ formatVal(lastValue(s.points)) }}</span>
            @let d = seriesDelta(s.points);
            <span class="dim-cell numeric">
              <span class="delta-inline"
                    [class.delta-positive]="d.cls === 'positive'"
                    [class.delta-negative]="d.cls === 'negative'">
                {{ d.val >= 0 ? '+' : '−' }}{{ formatVal(d.val < 0 ? -d.val : d.val) }}
                @if (d.pct !== null) {
                  ({{ d.pct >= 0 ? '+' : '' }}{{ d.pct }}%)
                }
              </span>
            </span>
          </div>
          @if (canExpandRow(s.dimensionLabel) && isExpanded(s.dimensionLabel)) {
            @for (sub of getSubEntrepots(s.dimensionLabel); track sub.subEntrepot) {
              <div class="dim-row dim-row--sub">
                <span class="dim-cell color-cell"></span>
                <span class="dim-cell grow ellipsis" [matTooltip]="sub.subEntrepot">
                  <mat-icon class="sub-icon">subdirectory_arrow_right</mat-icon>
                  {{ sub.subEntrepot }}
                </span>
                <span class="dim-cell numeric">{{ formatVal(sub.total) }}</span>
                <span class="dim-cell numeric on-disabled">—</span>
              </div>
            }
          }
        }
      </div>
    }

    <!-- Note historique partiel -->
    @if (payload()?.availableSince; as since) {
      @if (since > (payload()?.startDate ?? '')) {
        <div class="tiny on-disabled flex-row gap-4 align-center">
          <mat-icon class="info-icon">info_outline</mat-icon>
          <span>{{ 'dashboard_ui.evolution_valeur_partial_history' | trans:{ since: since } }}</span>
        </div>
      }
    }
  </div>
</div>
